import React, {useCallback, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {ForkygramCollaboratorMemberData} from 'types'
import {getFontFamily, getFontSize, getTextShadow} from 'utils'
import {IconFotoyu, IconVerifiedBadge, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import ForkygramTemplatePostProfileAvatar from './ForkygramTemplatePostProfileAvatar'
import {ForkygramTemplatePostProfileProps} from './ForkygramTemplatePostProfileProps'

interface StyledProfileProps {
  shouldUpdateGap: boolean
}

interface StyledIconProps {
  isCollab?: boolean
}

const StyledProfileContainer = styled.div<StyledProfileProps>`
  ${({shouldUpdateGap}) => ({gap: convertUnit(shouldUpdateGap ? 16 : 8)})};
  display: flex;
  flex: 1;
  align-items: center;
`

const StyledAvatarContainer = styled.div`
  position: relative;
  display: flex;
  margin-bottom: ${convertUnit(2)};
`

const StyledUsernameContainer = styled.div`
  flex: 1;
  align-self: flex-end;
  text-shadow: ${({theme}) => getTextShadow(theme, {opacity: 0.5})};
`

const StyledUsernameWrapper = styled.div`
  position: relative;
  align-items: center;
`

const StyledCollabMember = styled.span`
  display: block;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
  cursor: pointer;
  color: ${({theme}) => theme.white_1};
  font-size: ${getFontSize('s')};
  font-family: ${getFontFamily('regular')};
  line-height: ${convertUnit(17)};
  text-shadow: ${({theme}) => getTextShadow(theme, {opacity: 0.5})};
`

const StyledCollabMemberUsername = styled.strong`
  font-family: ${getFontFamily('bold')};
  padding-left: ${convertUnit(2.5)};
  :hover {
    text-decoration: underline;
  }
`

const StyledCollabWhitespace = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  cursor: pointer;
  flex: 1;
  height: ${convertUnit(15)};
  padding: ${convertUnit(2)} 0;
`

const StyledParagraph = styled(Paragraph)`
  position: relative;
  display: inline;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
  z-index: 1;
  margin-right: ${convertUnit(4)};
  text-shadow: ${({theme}) => getTextShadow(theme, {opacity: 0.5})};
  :hover {
    text-decoration: underline;
  }
`

const StyledIconContainer = styled.div`
  display: inline;
`

const StyledIconVerified = styled(IconVerifiedBadge)<StyledIconProps>`
  margin-bottom: ${convertUnit(-4)};
  margin-left: ${({isCollab}) => (isCollab ? convertUnit(4) : 0)};
`

const StyledIconFotoyu = styled(IconFotoyu)<StyledIconProps>`
  margin-bottom: ${convertUnit(-8)};
  margin-left: ${({isCollab}) => (isCollab ? convertUnit(4) : 0)};
`

export default function ForkygramTemplatePostProfile({
  collabOwnerData,
  collabMemberData,
  onClickAvatar,
  onFollowed,
}: ForkygramTemplatePostProfileProps) {
  const {translate} = useTranslation()
  const usernameRef = useRef<HTMLDivElement>(null)
  const stateShouldUpdateGap = useState(false)
  const shouldUpdateGap = stateShouldUpdateGap[0]

  const handleRenderBadge = useCallback(
    (data: ForkygramCollaboratorMemberData, isCollab?: boolean) => (
      <StyledIconContainer>
        {data.is_official ? (
          <StyledIconFotoyu isCollab={isCollab} width={isCollab ? 12 : 16} />
        ) : data.creator_status === 'verified' ? (
          <StyledIconVerified isCollab={isCollab} width={isCollab ? 12 : 16} />
        ) : (
          <></>
        )}
      </StyledIconContainer>
    ),
    [],
  )

  const handleRenderAvatarOwner = useMemo(
    () => (
      <ForkygramTemplatePostProfileAvatar
        usernameRef={usernameRef}
        stateShouldUpdateGap={stateShouldUpdateGap}
        data={collabOwnerData}
        size={collabMemberData ? 'compact' : 'default'}
        containerStyle={collabMemberData ? {left: 1.5, bottom: 8.5} : undefined}
        onClickAvatar={() => onClickAvatar()}
        onFollowed={onFollowed}
      />
    ),
    [
      collabMemberData,
      collabOwnerData,
      onClickAvatar,
      onFollowed,
      stateShouldUpdateGap,
    ],
  )

  const handleRenderAvatarMember = useMemo(
    () =>
      collabMemberData && (
        <ForkygramTemplatePostProfileAvatar
          data={collabMemberData}
          size="compact"
          onClickAvatar={() => onClickAvatar(true)}
          onFollowed={onFollowed}
        />
      ),
    [collabMemberData, onClickAvatar, onFollowed],
  )

  const handleRenderUsername = useMemo(
    () => (
      <StyledUsernameWrapper>
        <StyledParagraph
          className="pressable"
          fontWeight="bold"
          color="white_1"
          fontSize="m"
          onClick={() => onClickAvatar()}>
          @{collabOwnerData.username}
        </StyledParagraph>
        {handleRenderBadge(collabOwnerData)}
        {collabMemberData && (
          <StyledCollabWhitespace onClick={() => onClickAvatar(true)} />
        )}
      </StyledUsernameWrapper>
    ),
    [collabMemberData, collabOwnerData, handleRenderBadge, onClickAvatar],
  )

  const handleRenderCollabMember = useMemo(
    () =>
      collabMemberData && (
        <StyledCollabMember onClick={() => onClickAvatar(true)}>
          {translate('global:and')}
          <StyledCollabMemberUsername>
            @{collabMemberData.username}
            {handleRenderBadge(collabMemberData, true)}
          </StyledCollabMemberUsername>
        </StyledCollabMember>
      ),
    [collabMemberData, handleRenderBadge, onClickAvatar, translate],
  )

  return (
    <StyledProfileContainer shouldUpdateGap={shouldUpdateGap}>
      <StyledAvatarContainer>
        {handleRenderAvatarOwner}
        {handleRenderAvatarMember}
      </StyledAvatarContainer>
      <StyledUsernameContainer ref={usernameRef}>
        {handleRenderUsername}
        {handleRenderCollabMember}
      </StyledUsernameContainer>
    </StyledProfileContainer>
  )
}
